import { useEffect, useRef, useState } from "react";
import update from "immutability-helper";
import { FaEnvelopeOpenText, FaListUl } from "react-icons/fa";
import { ProductLibBanner } from "../../API";
import Card from "./dndCard";

interface CardState {
  cardsById: Record<string, ProductLibBanner>;
  cardsByIndex: ProductLibBanner[];
}

interface ContainerProps {
  list: ProductLibBanner[];
  isDragging: boolean;
  setRemoveId: (id: string) => void;
  updateBannerOrder: (bannerUpdate: ProductLibBanner[]) => void;
}

const buildCardData = (list: ProductLibBanner[]): CardState => {
  const cardsById: Record<string, ProductLibBanner> = {};
  const cardsByIndex: ProductLibBanner[] = [];

  list.forEach((item, i) => {
    cardsById[item.id] = item;
    cardsByIndex[i] = item;
  });

  return {
    cardsById,
    cardsByIndex,
  };
};

function Dnd(props: ContainerProps): JSX.Element {
  const [isDragging, setIsDragging] = useState(false);
  const [cardState, setCardState] = useState<CardState>(
    buildCardData(props.list)
  );
  const requestedFrame = useRef<number | undefined>(undefined);

  useEffect(() => {
    setCardState(buildCardData(props.list));
    setIsDragging(props.isDragging);
  }, [props]);

  const [tempData, setTempData] = useState<ProductLibBanner[]>([]);

  useEffect(() => {
    setTempData(cardState.cardsByIndex);
  }, [cardState]);

  useEffect(() => {
    if (!isDragging) {
      props.updateBannerOrder(tempData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  useEffect(() => {
    return () => {
      if (requestedFrame.current !== undefined) {
        cancelAnimationFrame(requestedFrame.current);
      }
    };
  }, []);

  const moveCard = (id: string, afterId: string): void => {
    if (!isDragging) return;
    const { cardsById, cardsByIndex } = cardState;
    const card = cardsById[id] as ProductLibBanner;
    const afterCard = cardsById[afterId] as ProductLibBanner;
    const cardIndex = cardsByIndex.indexOf(card);
    const afterIndex = cardsByIndex.indexOf(afterCard);
    const newCardState = update(cardState, {
      cardsByIndex: {
        $splice: [
          [cardIndex, 1],
          [afterIndex, 0, card],
        ],
      },
    });
    setCardState(newCardState);
    scheduleUpdate();
  };

  const scheduleUpdate = () => {
    if (!requestedFrame.current) {
      requestedFrame.current = requestAnimationFrame(drawFrame);
    }
  };

  const drawFrame = (): void => {
    setCardState((prevState) => ({ ...prevState }));
    requestedFrame.current = undefined;
  };

  const { cardsByIndex } = cardState;
  return (
    <>
      <div className="w-full px-5">
        {cardsByIndex.length > 0 ? (
          cardsByIndex.map((card, index) => (
            <div className="flex" key={card.id}>
              {isDragging && (
                <div className="flex items-center justify-center rounded-lg p-7 mb-5 gap-3">
                  <FaListUl />
                  {index + 1}
                </div>
              )}
              <div className="w-full">
                <Card
                  id={card.id}
                  data={card}
                  moveCard={moveCard}
                  setRemoveId={props.setRemoveId}
                  cardIsDragging={isDragging}
                />
              </div>
            </div>
          ))
        ) : (
          // Empty Banner
          <div className="flex items-center justify-center h-96">
            <div>
              <FaEnvelopeOpenText className="text-8xl text-gray-400 justify-self-center" />
              <div className="text-gray-400 my-5">ยังไม่มีข้อมูล Banner</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Dnd;
